import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { operationalInsightEndpoints, targetEndpoints } from '@/lib/api/endpoints';
import { operationalInsightKeys, targetKeys } from '@/lib/queryKeys';
import { useCallReport } from '@/lib/calls/hooks';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { usePurchasingEnabled } from '@/features/purchasing/visibility';
import { dateFilterToRange } from './dashboardRange';
import type { DateFilter } from './useDashboardStats';

export interface FinanceQueues {
  unbilled: {
    alertEnabled: boolean;
    thresholdDays: number;
    count: number;
    amount: number | null;
    rows: Array<{
      id: string;
      jobNumber: string;
      name: string;
      customer: string;
      value: number | null;
      completedAt: string | null;
      ageDays: number;
    }>;
  };
  accountsReceivable: {
    count: number;
    amount: number | null;
    buckets: {
      days0To30: { count: number; amount: number | null };
      days31To60: { count: number; amount: number | null };
      over60: { count: number; amount: number | null };
    };
    rows: Array<{
      id: string;
      invoiceNumber: string;
      customer: string;
      balance: number | null;
      ageDays: number;
      bucket: 'DAYS_0_30' | 'DAYS_31_60' | 'OVER_60';
      followUpAttempts: number;
    }>;
  };
}

export interface DayZeroInsight {
  isDayZero: boolean;
  counts: {
    jobs: number;
    appointments: number;
    estimates: number;
    proposals: number;
    invoices: number;
    payments: number;
    creditMemos: number;
    deposits: number;
    memberships: number;
    leads: number;
  };
  readiness: {
    jobTypes: {
      count: number;
      names: string[];
    };
    pricebook: {
      itemCount: number;
      costedItemCount: number;
    };
  };
}

export interface AutomationFailures {
  count: number;
  rows: Array<{
    id: string;
    type: string;
    channel: string;
    attempts: number;
    error: string | null;
    failedAt: string;
  }>;
}

export interface SchedulingInsight {
  period: { from: string; to: string };
  periodBoard: {
    total: number;
    done: number;
    active: number;
    toGo: number;
    unassigned: number;
  };
  tomorrow: {
    date: string;
    unassigned: Array<{ id: string; customer: string; startAt: string | null }>;
    unconfirmed: Array<{ id: string; customer: string; startAt: string | null }>;
  };
  pastWindow: Array<{
    id: string;
    customer: string;
    technician: string | null;
    lateMinutes: number;
  }>;
  capacity: CapacityInsight;
  nextCapacity: CapacityInsight;
}

export interface CapacityInsight {
  from: string | null;
  to: string | null;
  utilization: number;
  bookedMinutes: number;
  availableMinutes: number;
  rows: Array<{
    technicianId: string;
    name: string;
    bookedMinutes: number;
    availableMinutes: number;
    utilization: number;
    capacitySource: 'AVAILABILITY' | 'CAPACITY_FALLBACK';
  }>;
}

export interface OperationsDashboardInsight {
  generatedAt: string;
  timezone: string;
  period: {
    from: string;
    to: string;
    previousFrom: string;
    previousTo: string;
  };
  invoiceToPaid: null | {
    currentMedianDays: number | null;
    previousMedianDays: number | null;
    currentSampleSize: number;
    previousSampleSize: number;
  };
  quality: {
    firstTimeFixRate: number | null;
    firstTimeFixSampleSize: number;
    callbackRate: number | null;
    callbackCount: number;
    callbackEligible: number;
    previousCallbackRate: number | null;
  };
  economics: null | {
    byJobType: Array<{
      name: string;
      revenue: number;
      cost: number;
      visits: number;
      margin: number;
      marginPct: number | null;
    }>;
    billedHours: number;
    paidHours: number;
    laborEfficiency: number | null;
  };
  automation: {
    handled: number;
    failed: number;
    failures: Array<{
      id: string;
      type: string;
      channel: string;
      targetType: string;
      targetId: string;
      attempts: number;
      error: string | null;
      failedAt: string;
    }>;
  };
}

export interface ProcurementInsight {
  generatedAt: string;
  blockedCount: number;
  oldestOpenDays: number | null;
  requestedCount: number;
  overdueOrders: number;
  requirements: Array<{
    id: string;
    name: string;
    quantity: number;
    status: string;
    neededBy: string | null;
    createdAt: string;
  }>;
  purchaseOrders: Array<{
    id: string;
    orderNumber: string;
    vendorName: string;
    status: string;
    total: number;
    expectedAt: string | null;
  }>;
}

export interface RenewalInsight {
  period: { from: string; to: string };
  count: number;
  monthlyRecurringValueAtRisk: number | null;
  rows: Array<{
    id: string;
    membershipNumber: string;
    customer: string;
    status: 'ACTIVE' | 'PAST_DUE' | 'LAPSED';
    renewalAt: string | null;
    monthlyRecurringValue: number | null;
    paymentMethodHealth: string;
    outreachStatus: string;
  }>;
}

export interface ServiceCallInsight {
  quality: string;
  totals: {
    serviceCalls: number;
    opportunities: number;
    opportunityGeneration: number;
    estimatesPresented: number;
    decided: number;
    won: number;
    closeRate: number;
    soldValue: number | null;
    soldPerOpportunity: number | null;
    upsellCalls: number;
    upsellAttach: number;
  };
  byTechnician: Array<{
    technicianId: string;
    name: string;
    serviceCalls: number;
    opportunities: number;
    opportunityGeneration: number;
    decided: number;
    won: number;
    closeRate: number;
    soldValue: number | null;
    soldPerOpportunity: number | null;
    callbacks: number;
    callbackRate: number;
    upsellCalls: number;
    upsellAttach: number;
  }>;
}

export interface HomeOverviewInsight {
  period: { from: string; to: string };
  workSources: {
    totalRevenue: number | null;
    rows: Array<{ name: string; revenue: number | null; share: number }>;
  };
  iris: {
    bookings: number;
    voiceBookings: number;
    smsBookings: number;
    recoveredRevenue: number | null;
    recoveredCount: number;
    influencedRevenue: number | null;
  };
}

export interface TargetAttainmentInsight {
  periodStart: string;
  periodType: 'WEEK' | 'MONTH' | 'QUARTER';
  rows: Array<{
    metric: string;
    target: number;
    actual: number | null;
    attainmentPct: number | null;
    direction: 'HIGHER' | 'LOWER';
    targetMet: boolean | null;
    expectedByToday: number | null;
    periodProgress: number;
    paceState: 'NOT_STARTED' | 'INACTIVE' | 'AHEAD' | 'ON_PACE' | 'BEHIND';
    denominator: number | null;
    minimumDenominator: number | null;
    active: boolean;
    adjustments: Array<{ deltaValue: number; reason: string; createdAt: string }>;
  }>;
}

export type TargetPeriodType = TargetAttainmentInsight['periodType'];

export type TargetAttainmentByPeriod = Partial<Record<TargetPeriodType, TargetAttainmentInsight>>;

export function selectTargetAttainment(
  targets: TargetAttainmentByPeriod,
  metric: string,
  cadences: TargetPeriodType[],
) {
  for (const cadence of cadences) {
    const row = targets[cadence]?.rows.find((candidate) => candidate.metric === metric);
    if (row) return row;
  }
  return undefined;
}

function targetPeriodType(filter: DateFilter, from?: string, to?: string) {
  if (filter.period === 'today' || filter.period === 'this_week') return 'WEEK' as const;
  if (filter.period === 'this_month') return 'MONTH' as const;
  if (filter.period === 'all_time') return 'QUARTER' as const;
  if (
    filter.period === 'custom' &&
    filter.startDate?.endsWith('-01') &&
    filter.startDate.slice(0, 7) === filter.endDate?.slice(0, 7)
  ) {
    return 'MONTH' as const;
  }
  const days = from && to ? (new Date(to).getTime() - new Date(from).getTime()) / 86_400_000 : 0;
  if (days <= 7) return 'WEEK' as const;
  if (days <= 45) return 'MONTH' as const;
  return 'QUARTER' as const;
}

function dateKeyAtOffset(dateKey: string, days: number) {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(Date.UTC(year!, month! - 1, day! + days)).toISOString().slice(0, 10);
}

export function calendarAlignedTargetPeriod(
  from?: string,
  to?: string,
  currentDate?: string,
): TargetPeriodType | null {
  if (!from || !to) return null;
  const [year, month, day] = from.split('-').map(Number);
  const start = new Date(Date.UTC(year!, month! - 1, day!));
  const startDay = start.getUTCDay();
  const isMonday = startDay === 1;
  if (isMonday && dateKeyAtOffset(from, 6) === to) return 'WEEK';

  const monthEnd = new Date(Date.UTC(year!, month!, 0)).toISOString().slice(0, 10);
  const isMonthStart = day === 1;
  if (
    isMonthStart &&
    (to === monthEnd || (to === currentDate && from.slice(0, 7) === to.slice(0, 7)))
  ) {
    return 'MONTH';
  }

  const quarterEnd = new Date(Date.UTC(year!, Math.floor((month! - 1) / 3) * 3 + 3, 0))
    .toISOString()
    .slice(0, 10);
  const isQuarterStart = day === 1 && (month! - 1) % 3 === 0;
  if (isQuarterStart && to === quarterEnd) return 'QUARTER';
  return null;
}

export function useOperationalInsights(filter: DateFilter, canViewCalls = true) {
  const purchasingEnabled = usePurchasingEnabled();
  const timeZone = useCompanyTimezone();
  const now = new Date();
  const period = dateFilterToRange(filter, now, timeZone);
  const targetPeriod = targetPeriodType(filter, period.from, period.to);
  const currentDate = dateFilterToRange({ period: 'today' }, now, timeZone).to;
  const targetComparisonPeriod = calendarAlignedTargetPeriod(period.from, period.to, currentDate);
  const common = { showErrorToast: false, staleTime: 30_000 } as const;
  const dayZero = useApiQuery<DayZeroInsight>({
    ...common,
    queryKey: operationalInsightKeys.report('day-zero'),
    queryFn: () => apiGet<DayZeroInsight>(operationsClient, operationalInsightEndpoints.dayZero()),
  });
  const home = useApiQuery<HomeOverviewInsight>({
    ...common,
    queryKey: operationalInsightKeys.report('home-overview', period),
    queryFn: () =>
      apiGet<HomeOverviewInsight>(
        operationsClient,
        operationalInsightEndpoints.homeOverview(period),
      ),
  });
  const finance = useApiQuery<FinanceQueues>({
    ...common,
    queryKey: operationalInsightKeys.report('finance'),
    queryFn: () =>
      apiGet<FinanceQueues>(operationsClient, operationalInsightEndpoints.financeQueues()),
  });
  const automation = useApiQuery<AutomationFailures>({
    ...common,
    queryKey: operationalInsightKeys.report('automation'),
    queryFn: () =>
      apiGet<AutomationFailures>(
        operationsClient,
        operationalInsightEndpoints.automationFailures(),
      ),
  });
  const scheduling = useApiQuery<SchedulingInsight>({
    ...common,
    queryKey: operationalInsightKeys.report('scheduling', period),
    queryFn: () =>
      apiGet<SchedulingInsight>(operationsClient, operationalInsightEndpoints.scheduling(period)),
  });
  const renewals = useApiQuery<RenewalInsight>({
    ...common,
    queryKey: operationalInsightKeys.report('renewals', period),
    queryFn: () =>
      apiGet<RenewalInsight>(operationsClient, operationalInsightEndpoints.renewals(period)),
  });
  const procurement = useApiQuery<ProcurementInsight>({
    ...common,
    // Part requirements are purchasing data; the tile they feed is hidden with it.
    enabled: purchasingEnabled,
    queryKey: operationalInsightKeys.report('procurement'),
    queryFn: () =>
      apiGet<ProcurementInsight>(operationsClient, operationalInsightEndpoints.procurement()),
  });
  const serviceCalls = useApiQuery<ServiceCallInsight>({
    ...common,
    queryKey: operationalInsightKeys.report('service-calls', period),
    queryFn: () =>
      apiGet<ServiceCallInsight>(
        operationsClient,
        operationalInsightEndpoints.serviceCalls(period),
      ),
  });
  const targets = useApiQuery<TargetAttainmentInsight>({
    ...common,
    queryKey: targetKeys.attainment('TENANT', '', `${period.to ?? ''}:${targetPeriod}`),
    queryFn: () =>
      apiGet<TargetAttainmentInsight>(
        operationsClient,
        targetEndpoints.attainment({
          reference: period.to,
          scope: 'TENANT',
          periodType: targetPeriod,
        }),
      ),
  });
  const calls = useCallReport(
    {
      interval: 'custom',
      startDate: period.from,
      endDate: period.to,
    },
    { enabled: canViewCalls },
  );

  return {
    dayZero,
    home,
    finance,
    automation,
    scheduling,
    renewals,
    procurement,
    serviceCalls,
    targets,
    targetsMatchFilter: targetComparisonPeriod === targetPeriod,
    calls,
  };
}

export function useOperationsDashboard(filter: DateFilter) {
  const purchasingEnabled = usePurchasingEnabled();
  const timeZone = useCompanyTimezone();
  const now = new Date();
  const period = dateFilterToRange(filter, now, timeZone);
  const targetPeriod = targetPeriodType(filter, period.from, period.to);
  const currentTargetReference = dateFilterToRange({ period: 'today' }, now, timeZone).to;
  const qualityTargetPeriod = calendarAlignedTargetPeriod(
    period.from,
    period.to,
    currentTargetReference,
  );
  const common = {
    showErrorToast: false,
    staleTime: 30_000,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  } as const;
  const finance = useApiQuery<FinanceQueues>({
    ...common,
    queryKey: operationalInsightKeys.report('operations-finance'),
    queryFn: () =>
      apiGet<FinanceQueues>(operationsClient, operationalInsightEndpoints.financeQueues()),
  });
  const scheduling = useApiQuery<SchedulingInsight>({
    ...common,
    queryKey: operationalInsightKeys.report('operations-scheduling', period),
    queryFn: () =>
      apiGet<SchedulingInsight>(operationsClient, operationalInsightEndpoints.scheduling(period)),
  });
  const procurement = useApiQuery<ProcurementInsight>({
    ...common,
    // Feeds "Blocked on parts", which is hidden with purchasing.
    enabled: purchasingEnabled,
    queryKey: operationalInsightKeys.report('operations-procurement'),
    queryFn: () =>
      apiGet<ProcurementInsight>(operationsClient, operationalInsightEndpoints.procurement()),
  });
  const metrics = useApiQuery<OperationsDashboardInsight>({
    ...common,
    queryKey: operationalInsightKeys.report('operations-dashboard', period),
    queryFn: () =>
      apiGet<OperationsDashboardInsight>(
        operationsClient,
        operationalInsightEndpoints.operationsDashboard(period),
      ),
  });
  const home = useApiQuery<HomeOverviewInsight>({
    ...common,
    queryKey: operationalInsightKeys.report('operations-iris', period),
    queryFn: () =>
      apiGet<HomeOverviewInsight>(
        operationsClient,
        operationalInsightEndpoints.homeOverview(period),
      ),
  });

  const weeklyTargets = useApiQuery<TargetAttainmentInsight>({
    ...common,
    queryKey: targetKeys.attainment('TENANT', '', `${currentTargetReference}:WEEK`),
    queryFn: () =>
      apiGet<TargetAttainmentInsight>(
        operationsClient,
        targetEndpoints.attainment({
          reference: currentTargetReference,
          scope: 'TENANT',
          periodType: 'WEEK',
        }),
      ),
  });

  const monthlyTargets = useApiQuery<TargetAttainmentInsight>({
    ...common,
    queryKey: targetKeys.attainment('TENANT', '', `${currentTargetReference}:MONTH`),
    queryFn: () =>
      apiGet<TargetAttainmentInsight>(
        operationsClient,
        targetEndpoints.attainment({
          reference: currentTargetReference,
          scope: 'TENANT',
          periodType: 'MONTH',
        }),
      ),
  });

  const quarterlyTargets = useApiQuery<TargetAttainmentInsight>({
    ...common,
    queryKey: targetKeys.attainment('TENANT', '', `${currentTargetReference}:QUARTER`),
    queryFn: () =>
      apiGet<TargetAttainmentInsight>(
        operationsClient,
        targetEndpoints.attainment({
          reference: currentTargetReference,
          scope: 'TENANT',
          periodType: 'QUARTER',
        }),
      ),
  });

  const qualityTargets = useApiQuery<TargetAttainmentInsight>({
    ...common,
    enabled: qualityTargetPeriod != null,
    queryKey: targetKeys.attainment(
      'TENANT',
      '',
      `${period.to ?? ''}:${qualityTargetPeriod ?? 'NONE'}:operations-quality`,
    ),
    queryFn: () =>
      apiGet<TargetAttainmentInsight>(
        operationsClient,
        targetEndpoints.attainment({
          // Quality actuals respect the dashboard date filter, so their target
          // context must use the same period instead of the live-card period.
          reference: period.to,
          scope: 'TENANT',
          periodType: qualityTargetPeriod ?? 'MONTH',
        }),
      ),
  });

  return {
    period,
    finance,
    scheduling,
    procurement,
    metrics,
    home,
    targetPeriod,
    qualityTargetPeriod,
    qualityTargets,
    targets: {
      WEEK: weeklyTargets,
      MONTH: monthlyTargets,
      QUARTER: quarterlyTargets,
    },
  };
}
